import { memo, type NamedExoticComponent } from 'react';

import type { MeasurementKind, UnitCodeFor } from '../../contract/types';
import type { MeasurementFormatter } from '../../format/bundle';
import type { PrecisionContext } from '../../units/precision';
import { useFormat } from '../useFormat';
import { DEFAULT_EMPTY_VALUE, type EmptyProps, isBlankNumber } from './shared';

export type MeasurementProps<K extends MeasurementKind> = EmptyProps & {
  /** The number as the backend sent it. */
  readonly value: number | null | undefined;
  /**
   * The unit `value` is in, as the backend sent it.
   *
   * Required, and the only thing the component cannot work out for itself. The unit the *reader*
   * wants comes from their preferences; the unit the *payload* carries is a property of the
   * endpoint, so it has to be stated here. Not to be confused with a formatter's `unit`, which is
   * the other end of the same conversion — what the reader is shown.
   */
  readonly valueUnit: UnitCodeFor<K>;
  /** Where the value is being shown, which decides the precision. Defaults to `display`. */
  readonly context?: PrecisionContext;
};

/**
 * Builds the component for one measurement kind.
 *
 * Nine kinds share one implementation because they differ only in which formatter they reach for.
 * Written as a factory rather than nine copies so that a change to the empty-value rule or the
 * memo boundary lands in one place, and so a new kind cannot be added with a subtly different
 * prop contract.
 *
 * @param kind - The measurement kind, which selects the formatter and types `valueUnit`.
 * @param displayName - The component's name, as a reader of a React tree would expect to see it.
 * @returns The memoized component. It renders a bare string, so it drops into a table cell or a
 *   `<Typography>` without contributing an element of its own.
 */
export const createMeasurement = <K extends MeasurementKind>(
  kind: K,
  displayName: string
): NamedExoticComponent<MeasurementProps<K>> => {
  const Measurement = ({
    value,
    valueUnit,
    context,
    emptyValue = DEFAULT_EMPTY_VALUE,
  }: MeasurementProps<K>): JSX.Element => {
    const format = useFormat();

    if (isBlankNumber(value)) return <>{emptyValue}</>;

    // `format[kind]` is `MeasurementFormatter<K>` by construction, but indexing a concrete object
    // type with a generic key leaves TypeScript holding the union of all nine formatters, whose
    // call signatures intersect to `never`. The correlation is restated here.
    const formatter = format[kind] as MeasurementFormatter<K>;

    return <>{formatter.format(value, { valueUnit, context })}</>;
  };

  // Named on the memoized component rather than on the function inside it, which is what the other
  // five components do and what makes the name readable from outside — `memo` does not carry an
  // inner `displayName` outward, so naming only the function leaves `Distance.displayName`
  // undefined while `Money.displayName` answers.
  const Memoized = memo(Measurement);
  Memoized.displayName = displayName;

  return Memoized;
};
